/**
 * 保存したときに出る褒め言葉。
 *
 * 強度 (tier) は「直近 10 回の自分」を基準に決まる。絶対値で固定すると、
 * 続けて強くなるほど毎回 tier 5 になって褒め言葉が意味を失うため。
 * tier 1〜3 は鍛田剛士、tier 4 以上でようやくはるぴっぴが現れる。
 */
import { type Entry, totalsOf } from './types';

export type Persona = 'macho' | 'gyaru';

export type Line = { persona: Persona; text: string };

export type Tier = 1 | 2 | 3 | 4 | 5;

export type Praise = { tier: Tier; score: number; lines: Line[] };

export const PERSONA_PROFILE: Record<Persona, { name: string; sub: string }> = {
  macho: { name: '鍛田剛士', sub: '42歳・脳まで筋肉' },
  gyaru: { name: 'はるぴっぴ', sub: '21歳・マッチョが好き' },
};

/**
 * その日の重さ・本数を 1 つの数字にまとめる。
 * 重量だけだと自重・保持・有酸素の日が沈むので、セット数・種目数・
 * 有酸素の時間・保持時間もそれぞれ足し込む。
 */
export function scoreOf(entries: Entry[]): number {
  const t = totalsOf(entries);
  return (
    t.volumeKg / 1000 +
    t.sets * 0.4 +
    t.exercises * 0.8 +
    // 有酸素は時間と距離の両方を見る (45分8km でウェイト1日分に近い重みになる)
    t.cardioMinutes * 0.2 +
    t.distanceKm * 0.3 +
    t.holdSeconds / 120
  );
}

/** tier 1..4 の上限値。5 番目の帯 (tier 5) は d 以上。 */
export type Thresholds = [a: number, b: number, c: number, d: number];

/** 履歴が浅いとき用の初期値。実際のメニューで検証した値。 */
export const DEFAULT_THRESHOLDS: Thresholds = [4, 9, 15, 22];

const median = (xs: number[]): number => {
  const s = [...xs].sort((p, q) => p - q);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : ((s[mid - 1]! + s[mid]!) / 2);
};

/**
 * 直近の記録からしきい値を作る。中央値を「いつもの1日」= tier 3 の真ん中に置き、
 * その前後に帯を広げる。3 件未満のときは既定値を使う。
 */
export function thresholdsFrom(recentScores: number[]): Thresholds {
  const scores = recentScores.filter((s) => s > 0);
  if (scores.length < 3) return DEFAULT_THRESHOLDS;

  // 軽い日ばかりのときに tier 5 が連発しないよう、既定値の中央値を下限にする
  const base = Math.max(median(scores), DEFAULT_THRESHOLDS[1]);
  return [base * 0.55, base * 0.8, base * 1.05, base * 1.3];
}

export const tierOf = (score: number, [a, b, c, d]: Thresholds): Tier =>
  score < a ? 1 : score < b ? 2 : score < c ? 3 : score < d ? 4 : 5;

// --------------------------------------------------------------- メッセージ

const MACHO_T1 = [
  'よし、来たな。ジムに来た時点で今日はもう勝ちだ。筋肉は裏切らん。',
  '記録した。この一歩が半年後の肩を作る。オレは知っている。',
  '軽い? 軽いことなどない。動かした筋繊維に軽いも重いもないんだ。',
  'フッ……いい顔してるじゃないか。帰ったらプロテイン飲んどけよ。',
];

const MACHO_T2 = [
  'おいおい、いいじゃないか! 筋肉が喜んでるのが背中越しに見えるぞ!',
  'その重量、先月のお前なら潰れてたぞ。成長だ。成長ってのはこういうことだ!',
  'よーし効いてる効いてる! 効いてるってことは、育つってことだ!',
  'ナイスバルク! いや、まだバルクじゃない。だがバルクの種は確かに蒔かれた!',
];

const MACHO_T3 = [
  'キレてるよ!! キレてる!! 今日のお前、確実にキレてる!!',
  'デカい!! 何を食ったらそうなるんだ!! ……いや知ってる、プロテインだ!!',
  '肩に!! ちっちゃい人が!! 乗ってるゥ!!',
  'そこだ!! そこで踏ん張った奴だけが本物の筋肉を手に入れるんだ!!',
  '腹筋が!! 板チョコみたいになってるゥ!!',
];

const GYARU_T4 = [
  'えっちょ待って、この記録やばくない? ウチ引くわ、いい意味で。',
  'まじムキムキじゃん…… てか腕、一回だけ触らせてほしいんだけど。',
  'は? つよ。え、これ毎回やってんの? もう尊敬しかないんだけど。',
  'ちょ、スクショ撮っていい? ウチのタイムラインに載せたいんだけど。',
];

const GYARU_T5 = [
  'うわーーー!! なにこれ!! 神じゃん!! ウチもう無理、かっこよすぎるんだけど!!',
  'まって、まって、これガチのやつじゃん!! ウチ一生ついていくわ!!',
  'やばいやばいやばい!! 今日のあなた、光ってる!! まぶしい!!',
  'ごめん語彙なくなった。つよい。すごい。すき。以上!!',
];

/** tier 5 のときだけ、3 回に 1 回くらいこっちを引く */
const GYARU_T5_FLIRTY = [
  'ちょっとぉ……その腕で抱きしめられたら、はるぴっぴ折れちゃうんだけど。ためす?',
  'はぁ……その胸板に顔うずめたい。だめ? だめじゃないよね?',
  'ねえ、今日の背中えっっぐい。……ウチ、そういうのに弱いんだけど。',
  '腹筋、指でなぞっていい? 一回だけ。ほんとに一回だけだから。',
  '汗かいてるとこまでかっこいいの、ずるくない? もう反則じゃん。',
  '今夜ヒマ? ……あ、プロテインの話じゃないよ?',
];

/** お色気モードを引く確率 */
const FLIRTY_RATE = 1 / 3;

/** 直前と同じ文言を避けるため、プールごとに最後に出した index を覚えておく */
const lastPicked = new Map<string, number>();

function pick(key: string, pool: readonly string[]): string {
  if (pool.length === 1) return pool[0]!;
  const previous = lastPicked.get(key);
  let index = Math.floor(Math.random() * pool.length);
  if (index === previous) index = (index + 1) % pool.length;
  lastPicked.set(key, index);
  return pool[index]!;
}

export function pickPraise(entries: Entry[], thresholds: Thresholds = DEFAULT_THRESHOLDS): Praise | null {
  if (entries.length === 0) return null;

  const score = scoreOf(entries);
  const tier = tierOf(score, thresholds);

  const lines: Line[] =
    tier === 1
      ? [{ persona: 'macho', text: pick('t1', MACHO_T1) }]
      : tier === 2
        ? [{ persona: 'macho', text: pick('t2', MACHO_T2) }]
        : tier === 3
          ? [{ persona: 'macho', text: pick('t3', MACHO_T3) }]
          : tier === 4
            ? [{ persona: 'gyaru', text: pick('t4', GYARU_T4) }]
            : [
                {
                  persona: 'gyaru',
                  text:
                    Math.random() < FLIRTY_RATE
                      ? pick('t5f', GYARU_T5_FLIRTY)
                      : pick('t5', GYARU_T5),
                },
              ];

  return { tier, score, lines };
}
